import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthenticatedUserModel } from '../common/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private router: Router, private cookieService: CookieService) {}

  private cachedUserData: AuthenticatedUserModel | null = null;
  private authServiceSubject = new Subject<any>();

  setUserCache(user: AuthenticatedUserModel): void {
    this.cachedUserData = user;
  }

  invalidateUserCache(): void {
    this.cachedUserData = null;
  }

  getClientUserData(): AuthenticatedUserModel {
    return this.cachedUserData ?? new AuthenticatedUserModel();
  }
  getExpirationTime() {
    return this.getClientUserData().expiration;
  }
  getTokenLifeTime() {
    return this.getClientUserData().expirationInMinutes;
  }
  getMaxFileSize() {
    return this.getClientUserData().maxMbFileDownload;
  }

  isUserAllowForEmail() {
    return this.getClientUserData().isDownloadEmailEnabled === true;
  }

  isUserAllowForDownload() {
    return this.getClientUserData().isDownloadDirectEnabled === true;
  }

  // generic method different type data can be thown via subject
  setSubject(data: any) {
    this.authServiceSubject.next(data);
  }
  clearSubject() {
    return this.authServiceSubject.next(undefined);
  }
  // difffent type of data can be get from observable
  getSubject(): Observable<any> {
    return this.authServiceSubject.asObservable();
  }
  private readonly storageKey = 'userData';

  private saveToStorage(userData: AuthenticatedUserModel) {
    sessionStorage.setItem(this.storageKey, JSON.stringify({
      userGuid: userData.userGuid,
      loggedInName: userData.loggedInName,
      iManageUserId: userData.iManageUserId,
      expiration: userData.expiration,
      expirationInMinutes: userData.expirationInMinutes,
      maxMbFileDownload: userData.maxMbFileDownload,
      isDownloadEmailEnabled: userData.isDownloadEmailEnabled,
      isDownloadDirectEnabled: userData.isDownloadDirectEnabled,
    }));
  }

  setAuthenticatedUser(userData: AuthenticatedUserModel) {
    this.setUserCache(userData);
    this.saveToStorage(userData);
  }
  restoreUserFromStorage(): boolean {
    const stored = sessionStorage.getItem(this.storageKey);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data.userGuid) {
          const userData = Object.assign(new AuthenticatedUserModel(), data);
          this.setUserCache(userData);
          return true;
        }
      } catch {
        sessionStorage.removeItem(this.storageKey);
      }
    }
    return false;
  }
  updateRefreshToken(userData: AuthenticatedUserModel) {
    const existingUserData = this.getClientUserData();
    existingUserData.expiration = userData.expiration;
    existingUserData.expirationInMinutes = userData.expirationInMinutes;
    this.setUserCache(existingUserData);
    this.saveToStorage(existingUserData);
  }
  logout() {
    const returnUrl =
      this.router.url.indexOf('search=true') > -1
        ? this.router.url.split('?')[0]
        : this.router.url;
    this.invalidateUserCache();
    sessionStorage.removeItem('userData');
    this.setSubject(null);
    this.cookieService.deleteAll();
    this.cookieService.deleteAll('/');
    this.deleteCookies();
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl,
      },
    });
  }
  deleteCookies() {
    const res = document.cookie;
    if (res !== '') {
      const multiple: string[] = res.split(';');
      if (multiple.length > 0) {
        multiple.forEach((cookie) => {
          const key = cookie.split('=');
          document.cookie =
            key[0] + ' =; expires = Thu, 01 Jan 1970 00:00:00 UTC';
        });
      }
    }
  }
}
